<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import PipelinesApi from 'dashboard/api/pipelines';

const store = useStore();
const { currentAccount } = useAccount();

const isSaving = ref(false);
const tokenCopied = ref(false);

const form = ref({
  enabled: false,
  instagram_inbox_id: null,
  score_threshold: 6,
  scoring_criteria: '',
  auto_label: 'lead-instagram',
  whatsapp_notify_inbox_id: null,
  whatsapp_notify_numbers: '',
  comment_pipeline_id: null,
  deep_media_analysis: false,
});

const scoreOptions = [5, 6, 7, 8, 9];

const instagramInboxes = computed(() =>
  store.getters['inboxes/getInboxes'].filter(
    i => i.channel_type === INBOX_TYPES.INSTAGRAM
  )
);

const pipelines = ref([]);

const whatsappInboxes = computed(() =>
  store.getters['inboxes/getInboxes'].filter(
    i => i.channel_type === INBOX_TYPES.WHATSAPP
  )
);

const extensionToken = computed(
  () => currentAccount.value?.settings?.instagram_lead_config?.extension_token || ''
);

function loadConfig() {
  const config = currentAccount.value?.settings?.instagram_lead_config;
  if (!config) return;
  form.value = {
    enabled: config.enabled ?? false,
    instagram_inbox_id: config.instagram_inbox_id ?? null,
    score_threshold: config.score_threshold ?? 6,
    scoring_criteria: config.scoring_criteria ?? '',
    auto_label: config.auto_label ?? 'lead-instagram',
    whatsapp_notify_inbox_id: config.whatsapp_notify_inbox_id ?? null,
    whatsapp_notify_numbers: Array.isArray(config.whatsapp_notify_numbers)
      ? config.whatsapp_notify_numbers.join(', ')
      : (config.whatsapp_notify_numbers ?? ''),
    comment_pipeline_id: config.comment_pipeline_id ?? null,
    deep_media_analysis: config.deep_media_analysis ?? false,
  };
}

async function copyToken() {
  await navigator.clipboard.writeText(extensionToken.value);
  tokenCopied.value = true;
  setTimeout(() => { tokenCopied.value = false; }, 2000);
}

async function save() {
  isSaving.value = true;
  try {
    const numbersArray = form.value.whatsapp_notify_numbers
      .split(',')
      .map(n => n.trim())
      .filter(n => n.length > 0);

    await store.dispatch('accounts/update', {
      instagram_lead_config: {
        ...form.value,
        whatsapp_notify_numbers: numbersArray,
      },
    });
    useAlert('Configurações salvas com sucesso!');
  } catch {
    useAlert('Erro ao salvar configurações.');
  } finally {
    isSaving.value = false;
  }
}

onMounted(async () => {
  store.dispatch('inboxes/get');
  const res = await PipelinesApi.list().catch(() => null);
  pipelines.value = res?.data?.payload || [];
  loadConfig();
});
</script>

<template>
  <div class="mx-auto max-w-2xl py-6">
    <div class="mb-6 flex items-center gap-3">
      <span class="i-lucide-instagram text-2xl text-pink-500" />
      <div>
        <h1 class="text-lg font-semibold text-n-slate-12">
          Instagram Lead Intelligence
        </h1>
        <p class="text-sm text-n-slate-10">
          Qualifique novos seguidores automaticamente com IA e adicione os melhores leads ao seu pipeline de vendas.
        </p>
      </div>
    </div>

    <div class="space-y-4 rounded-xl border border-n-alpha-3 bg-n-solid-1 p-5">

      <!-- Enable toggle -->
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm font-medium text-n-slate-12">Ativar análise automática</p>
          <p class="text-xs text-n-slate-10">
            Quando ativo, seguidores recebidos pela extensão serão analisados pelo Gemini automaticamente.
          </p>
        </div>
        <button
          type="button"
          :class="[
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none',
            form.enabled ? 'bg-n-brand' : 'bg-n-alpha-3',
          ]"
          @click="form.enabled = !form.enabled"
        >
          <span
            :class="[
              'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
              form.enabled ? 'translate-x-6' : 'translate-x-1',
            ]"
          />
        </button>
      </div>

      <hr class="border-n-alpha-3" />

      <!-- Extension token -->
      <div>
        <label class="mb-1 block text-sm font-medium text-n-slate-12">
          Token da Extensão Chrome
        </label>
        <p class="mb-2 text-xs text-n-slate-10">
          Use este token na configuração da extensão SKY AI — Instagram Leads no Chrome.
        </p>
        <div class="flex gap-2">
          <input
            :value="extensionToken"
            type="text"
            readonly
            class="w-full rounded-lg border border-n-alpha-3 bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-11 focus:outline-none font-mono"
          />
          <button
            type="button"
            class="flex-shrink-0 rounded-lg border border-n-alpha-3 px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-2 transition"
            @click="copyToken"
          >
            <span :class="tokenCopied ? 'i-lucide-check text-emerald-500' : 'i-lucide-copy'" />
          </button>
        </div>
      </div>

      <hr class="border-n-alpha-3" />

      <!-- Instagram inbox selector -->
      <div>
        <label class="mb-1 block text-sm font-medium text-n-slate-12">
          Perfil do Instagram monitorado
        </label>
        <p class="mb-2 text-xs text-n-slate-10">
          Selecione qual caixa de entrada Instagram receberá os seguidores captados pela extensão.
        </p>
        <select
          v-model="form.instagram_inbox_id"
          class="w-full rounded-lg border border-n-alpha-3 bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
        >
          <option :value="null">Todos os perfis Instagram</option>
          <option v-for="inbox in instagramInboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
        <p v-if="instagramInboxes.length === 0" class="mt-1 text-xs text-amber-500">
          Nenhuma caixa de entrada Instagram encontrada neste account.
        </p>
      </div>

      <hr class="border-n-alpha-3" />

      <!-- Score threshold -->
      <div>
        <label class="mb-1 block text-sm font-medium text-n-slate-12">
          Score mínimo para notificar
        </label>
        <p class="mb-2 text-xs text-n-slate-10">
          Apenas leads com score igual ou acima deste valor serão adicionados ao pipeline e notificados.
        </p>
        <div class="flex gap-2">
          <button
            v-for="score in scoreOptions"
            :key="score"
            type="button"
            :class="[
              'rounded-lg border px-3 py-1.5 text-sm font-medium transition',
              form.score_threshold === score
                ? 'border-n-brand bg-n-brand/10 text-n-brand'
                : 'border-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-2',
            ]"
            @click="form.score_threshold = score"
          >
            {{ score }}+
          </button>
        </div>
      </div>

      <hr class="border-n-alpha-3" />

      <!-- Scoring criteria -->
      <div>
        <label class="mb-1 block text-sm font-medium text-n-slate-12">
          Critério de qualificação
        </label>
        <p class="mb-2 text-xs text-n-slate-10">
          Descreva seu negócio e o perfil ideal de cliente. O Gemini usará isso para calcular o score de cada seguidor.
        </p>
        <textarea
          v-model="form.scoring_criteria"
          rows="4"
          class="w-full rounded-lg border border-n-alpha-3 bg-n-alpha-1 p-3 text-sm text-n-slate-12 placeholder-n-slate-9 focus:border-n-brand focus:outline-none"
          placeholder="Ex: Somos uma agência de automação de atendimento focada em PMEs do setor de saúde que faturam entre R$50k e R$1M/mês. O cliente ideal é um gestor ou dono de negócio com operação ativa de vendas..."
        />
      </div>

      <hr class="border-n-alpha-3" />

      <!-- Auto label -->
      <div>
        <label class="mb-1 block text-sm font-medium text-n-slate-12">
          Label automática
        </label>
        <p class="mb-2 text-xs text-n-slate-10">
          Label aplicada automaticamente nos contatos qualificados. Deixe em branco para não aplicar.
        </p>
        <input
          v-model="form.auto_label"
          type="text"
          class="w-full rounded-lg border border-n-alpha-3 bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
          placeholder="lead-instagram"
        />
      </div>

      <hr class="border-n-alpha-3" />

      <!-- WhatsApp notification inbox -->
      <div>
        <label class="mb-1 block text-sm font-medium text-n-slate-12">
          Caixa WhatsApp para notificação
        </label>
        <p class="mb-2 text-xs text-n-slate-10">
          Caixa de entrada Evolution que vai disparar a mensagem quando um lead for qualificado.
        </p>
        <select
          v-model="form.whatsapp_notify_inbox_id"
          class="w-full rounded-lg border border-n-alpha-3 bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
        >
          <option :value="null">Não notificar via WhatsApp</option>
          <option v-for="inbox in whatsappInboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <div v-if="form.whatsapp_notify_inbox_id">
        <label class="mb-1 block text-sm font-medium text-n-slate-12">
          Números para notificar
        </label>
        <p class="mb-2 text-xs text-n-slate-10">
          Números que receberão a notificação com score e sugestão de mensagem. Separe por vírgula.
        </p>
        <input
          v-model="form.whatsapp_notify_numbers"
          type="text"
          class="w-full rounded-lg border border-n-alpha-3 bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
          placeholder="5511999999999, 5521988888888"
        />
      </div>

      <hr class="border-n-alpha-3" />

      <!-- Comment pipeline -->
      <div>
        <label class="mb-1 block text-sm font-medium text-n-slate-12">
          Funil para comentários em posts
        </label>
        <p class="mb-2 text-xs text-n-slate-10">
          Quando alguém comentar em um post, será criado automaticamente um card neste funil (sem duplicatas).
        </p>
        <select
          v-model="form.comment_pipeline_id"
          class="w-full rounded-lg border border-n-alpha-3 bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
        >
          <option :value="null">Não adicionar ao funil</option>
          <option v-for="p in pipelines" :key="p.id" :value="p.id">
            {{ p.name }}
          </option>
        </select>
      </div>

      <hr class="border-n-alpha-3" />

      <!-- Deep media analysis -->
      <div class="flex items-start justify-between gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-12">
            Análise de mídia profunda
          </label>
          <p class="mt-1 text-xs text-n-slate-10">
            Analisa fotos de perfil, imagens e vídeos dos posts via Gemini Vision.
            Gera dados de gênero, aparência, descrições visuais e transcrições de áudio.
            <span class="font-medium text-amber-500">Deixa a análise mais lenta e consome mais créditos de API.</span>
          </p>
        </div>
        <button
          type="button"
          class="mt-1 flex-shrink-0 rounded-full transition-colors duration-200"
          :class="form.deep_media_analysis ? 'bg-n-brand' : 'bg-n-alpha-3'"
          style="width:44px;height:24px;padding:2px"
          @click="form.deep_media_analysis = !form.deep_media_analysis"
        >
          <span
            class="block size-5 rounded-full bg-white shadow transition-transform duration-200"
            :style="form.deep_media_analysis ? 'transform:translateX(20px)' : 'transform:translateX(0)'"
          />
        </button>
      </div>

      <hr class="border-n-alpha-3" />

      <!-- How it works -->
      <div class="rounded-lg bg-n-alpha-2 p-3">
        <p class="mb-2 text-xs font-semibold text-n-slate-11">Como funciona:</p>
        <ol class="space-y-1 text-xs text-n-slate-10">
          <li>1. Alguém segue sua conta no Instagram</li>
          <li>2. A extensão Chrome detecta o novo seguidor e envia para cá</li>
          <li>3. O perfil público é analisado pelo Gemini</li>
          <li>4. Se score ≥ {{ form.score_threshold }}, o contato vai para o pipeline de vendas</li>
          <li>5. Notificação chega no WhatsApp com link, score e sugestão de mensagem</li>
        </ol>
      </div>

      <!-- Save -->
      <div class="flex justify-end">
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white hover:bg-n-brand/90 disabled:opacity-50"
          :disabled="isSaving"
          @click="save"
        >
          <span v-if="isSaving" class="i-lucide-loader-2 animate-spin text-sm" />
          {{ isSaving ? 'Salvando...' : 'Salvar configurações' }}
        </button>
      </div>
    </div>
  </div>
</template>
